import React, { useState } from 'react';
import { Developer } from '../types/developer';
import { formatHourlyRate, formatSkills } from '../utils/airtableUtils';

type SortField = 'name' | 'type' | 'experienceLevel' | 'hourlyRate';
type SortDirection = 'asc' | 'desc';

interface Props {
  developers: Developer[];
  selectedDevelopers: Developer[];
  onSelectDeveloper: (developer: Developer) => void;
  onDeselectDeveloper: (developerId: string) => void;
  onSelectAll: () => void;
  onDeselectAll: () => void;
  isLoading?: boolean;
  onRowClick?: (developer: Developer) => void;
  highlightSelection?: boolean;
}

const DeveloperTable: React.FC<Props> = ({
  developers,
  selectedDevelopers,
  onSelectDeveloper,
  onDeselectDeveloper,
  onSelectAll,
  onDeselectAll,
  isLoading = false,
  onRowClick,
  highlightSelection = false
}) => {
  const [sortField, setSortField] = useState<SortField>('hourlyRate');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  const isSelected = (developerId: string) => {
    return selectedDevelopers.some(dev => dev.id === developerId);
  };

  const isAllSelected = developers.length > 0 && selectedDevelopers.length === developers.length;
  const isPartiallySelected = selectedDevelopers.length > 0 && selectedDevelopers.length < developers.length;

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortedDevelopers = () => {
    return [...developers].sort((a, b) => {
      let aValue: any, bValue: any;

      switch (sortField) {
        case 'name':
          aValue = a.name.toLowerCase();
          bValue = b.name.toLowerCase();
          break;
        case 'type':
          aValue = a.type.toLowerCase();
          bValue = b.type.toLowerCase();
          break;
        case 'experienceLevel':
          const levels = ['Junior', 'Mid-level', 'Senior', 'Lead'];
          aValue = levels.indexOf(a.experienceLevel);
          bValue = levels.indexOf(b.experienceLevel);
          break;
        case 'hourlyRate':
          aValue = a.hourlyRate;
          bValue = b.hourlyRate;
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const getSortIcon = (field: SortField) => {
    if (sortField !== field) {
      return (
        <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
        </svg>
      );
    }
    
    return sortDirection === 'asc' ? (
      <svg className="w-4 h-4 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
      </svg>
    ) : (
      <svg className="w-4 h-4 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
      </svg>
    );
  };

  if (developers.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="text-gray-500 text-lg mb-2">Inga profiler hittades</div>
        <div className="text-gray-400 text-sm">
          Hittar du inte vad du söker? Kontakta oss så hjälper vi dig.
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Mobile Card Layout */}
      <div className="block md:hidden bg-white">
        <div className="relative w-full overflow-auto" style={{ maxHeight: '60vh' }}>
          <div className="space-y-2 pt-3 px-3 pb-0">
            {getSortedDevelopers().map((developer) => {
              const selected = isSelected(developer.id);
              return (
                <div 
                  key={developer.id} 
                  className={`rounded-lg shadow border p-3 bg-white flex flex-col gap-1 cursor-pointer hover:bg-gray-50 transition-colors ${selected ? 'border-primary-500 bg-primary-50' : 'border-gray-200'}`}
                  onClick={e => {
                    if ((e.target as HTMLElement).closest('input')) return;
                    if (onRowClick) onRowClick(developer);
                  }}
                >
                  {/* Top row with checkbox, photo, name, and price */}
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2 flex-1">
                      <input
                        type="checkbox"
                        checked={selected}
                        onChange={(e) => {
                          e.stopPropagation();
                          if (selected) {
                            onDeselectDeveloper(developer.id);
                          } else {
                            onSelectDeveloper(developer);
                          }
                        }}
                        disabled={isLoading}
                        className={`h-3 w-3 text-primary-600 focus:ring-primary-500 border-gray-300 rounded transition-all duration-300 ${
                          highlightSelection 
                            ? 'ring-2 ring-blue-500 ring-opacity-70 shadow-blue-200 shadow-lg animate-pulse' 
                            : ''
                        }`}
                      />
                      {developer.photo ? (
                        <img src={developer.photo} alt={developer.name} className="w-8 h-8 rounded-full object-cover border border-primary-100" />
                      ) : (
                        <div className="w-8 h-8 rounded-full bg-gray-300 flex items-center justify-center">
                          <span className="text-gray-600 font-semibold text-xs">{developer.name.split(' ').map(n => n[0]).join('').toUpperCase()}</span>
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-sm text-primary-600 truncate">{developer.name}</div>
                        <div className="text-xs text-black">{developer.type}</div>
                      </div>
                    </div>
                    {/* Price in top right corner */}
                    <div className="font-semibold text-sm text-black ml-2">
                      {formatHourlyRate(developer.hourlyRate)}
                    </div>
                  </div>
                  
                  {/* Level and experience */}
                  <div className="flex flex-wrap gap-1 text-xs">
                    <span className="font-medium text-black">Nivå:</span>
                    <span className="text-black">{developer.experienceLevel}</span>
                    <span className="text-black">- {developer.yearsExperience} år</span>
                  </div>
                  
                  {/* Skills */}
                  <div className="flex flex-wrap gap-1 text-xs">
                    <span className="font-medium text-black">Kompetenser:</span>
                    <span className="text-black">{formatSkills(developer.skills)}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
      {/* Desktop Table Layout */}
      <div className="hidden md:block bg-white">
        <div className="relative w-full overflow-auto" style={{ maxHeight: '60vh' }}>
          <table className="w-full divide-y divide-gray-200">
            <thead className="bg-gray-50 sticky top-0 z-10">
              <tr>
                {/* Select All Checkbox */}
                <th className="w-12 px-4 md:px-6 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      checked={isAllSelected}
                      ref={(input) => {
                        if (input) input.indeterminate = isPartiallySelected;
                      }}
                      onChange={() => {
                        if (isAllSelected) {
                          onDeselectAll();
                        } else {
                          onSelectAll();
                        }
                      }}
                      disabled={isLoading}
                      className={`h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded transition-all duration-300 ${
                        highlightSelection 
                          ? 'ring-2 ring-blue-500 ring-opacity-70 shadow-blue-200 shadow-lg animate-pulse' 
                          : ''
                      }`}
                    />
                  </div>
                </th>
                
                {/* Name - Sortable */}
                <th 
                  className="px-4 md:px-6 py-2 text-left text-xs font-medium text-gray-500 tracking-wider whitespace-nowrap cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('name')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Namn</span>
                    {getSortIcon('name')}
                  </div>
                </th>
                
                {/* Type - Sortable */}
                <th 
                  className="px-4 md:px-6 py-2 text-left text-xs font-medium text-gray-500 tracking-wider whitespace-nowrap cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('type')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Typ</span>
                    {getSortIcon('type')}
                  </div>
                </th>
                
                {/* Experience Level - Sortable */}
                <th 
                  className="px-4 md:px-6 py-2 text-left text-xs font-medium text-gray-500 tracking-wider whitespace-nowrap cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('experienceLevel')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Nivå</span>
                    {getSortIcon('experienceLevel')}
                  </div>
                </th>
                
                <th className="px-4 md:px-6 py-2 text-left text-xs font-medium text-gray-500 tracking-wider">Kompetenser</th>
                
                {/* Rate - Sortable */}
                <th 
                  className="px-4 md:px-6 py-2 text-left text-xs font-medium text-gray-500 tracking-wider whitespace-nowrap cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('hourlyRate')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Pris/h</span>
                    {getSortIcon('hourlyRate')}
                  </div>
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {getSortedDevelopers().map((developer) => {
                const selected = isSelected(developer.id);
                return (
                  <tr
                    key={developer.id}
                    className={`hover:bg-gray-50 transition-colors ${selected ? 'bg-primary-50' : ''} cursor-pointer`}
                    onClick={e => {
                      if ((e.target as HTMLElement).closest('input')) return;
                      if (onRowClick) onRowClick(developer);
                    }}
                  >
                    {/* Selection Checkbox */}
                    <td className="w-12 px-6 py-2 whitespace-nowrap">
                      <input
                        type="checkbox"
                        checked={selected}
                        onChange={(e) => {
                          e.stopPropagation();
                          if (selected) {
                            onDeselectDeveloper(developer.id);
                          } else {
                            onSelectDeveloper(developer);
                          }
                        }}
                        disabled={isLoading}
                        className={`h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded transition-all duration-300 ${
                          highlightSelection 
                            ? 'ring-2 ring-blue-500 ring-opacity-70 shadow-blue-200 shadow-lg animate-pulse' 
                            : ''
                        }`}
                      />
                    </td>
                    
                    {/* Developer Name */}
                    <td className="px-6 py-2 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                          {developer.photo ? (
                            <img
                              className="h-10 w-10 rounded-full object-cover"
                              src={developer.photo}
                              alt={developer.name}
                            />
                          ) : (
                            <div className="h-10 w-10 rounded-full bg-gray-300 flex items-center justify-center">
                              <span className="text-gray-600 font-semibold text-sm">
                                {developer.name.split(' ').map(n => n[0]).join('').toUpperCase()}
                              </span>
                            </div>
                          )}
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-primary-600 hover:text-primary-800">
                            {developer.name}
                          </div>
                        </div>
                      </div>
                    </td>

                    {/* Type */}
                    <td className="px-6 py-2 whitespace-nowrap">
                      <span className="inline-flex px-2 py-1 text-xs text-gray-800">
                        {developer.type}
                      </span>
                    </td>

                    {/* Experience Level */}
                    <td className="px-6 py-2 whitespace-nowrap">
                      <span className="text-xs text-gray-800">
                        {developer.experienceLevel} - {developer.yearsExperience} år
                      </span>
                    </td>

                    {/* Skills */}
                    <td className="px-6 py-2">
                      <div className="text-xs text-gray-800">
                        {formatSkills(developer.skills.slice(0, 3))}
                        {developer.skills.length > 3 && (
                          <span className="text-gray-500 ml-1">
                            ...
                          </span>
                        )}
                      </div>
                    </td>

                    {/* Rate */}
                    <td className="px-6 py-2 whitespace-nowrap">
                      <div className="text-sm font-semibold text-black">
                        {formatHourlyRate(developer.hourlyRate)}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
};

export default DeveloperTable; 